import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Typography,
  Paper,
  Box,
  Button,
  TextField,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Snackbar,
  Alert,
  TablePagination,
  CircularProgress,
  SelectChangeEvent,
  Chip,
  useTheme,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  InputAdornment
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Category as CategoryIcon
} from '@mui/icons-material';
import SidebarLayout from '../../components/layout/SidebarLayout';
import CategoriasService, {
  Categoria,
  CategoriaCreate,
  CategoriaUpdate,
  CategoriaFilter,
} from '../../services/categoriasService';
import SetoresService from '../../services/setoresService';

// Interface para entidades de referência
interface EntidadeSimples {
  id: number;
  nome: string;
}

const CategoriasPage: React.FC = () => {
  const theme = useTheme();
  
  // Estado de listagem
  const [categorias, setCategorias] = useState<Categoria[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  // Estado de referências
  const [setores, setSetores] = useState<EntidadeSimples[]>([]);
  const [categoriasPrincipais, setCategoriasPrincipais] = useState<EntidadeSimples[]>([]);
  const [loadingReferencias, setLoadingReferencias] = useState(false);

  // Estado de filtros
  const [filter, setFilter] = useState<CategoriaFilter>({});
  
  // Estado de formulários
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [currentCategoria, setCurrentCategoria] = useState<Categoria | null>(null);
  const [newCategoria, setNewCategoria] = useState<CategoriaCreate>({ nome: '' });
  const [editCategoria, setEditCategoria] = useState<CategoriaUpdate>({});

  // Estado de mensagens
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error',
  });

  // Adicionar ref para debounce
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Carregar todas as referências necessárias
  const carregarReferencias = useCallback(async () => {
    try {
      setLoadingReferencias(true);
      
      // Buscar setores reais da API
      const setoresData = await SetoresService.getAllSetores();
      setSetores(setoresData);
      
      // Buscar categorias para uso como "categoria principal"
      const categoriasData = await CategoriasService.getCategorias({}, 0, 100);
      setCategoriasPrincipais(categoriasData);
    } catch (error) {
      console.error('Erro ao carregar referências:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao carregar dados de referência',
        severity: 'error',
      });
    } finally {
      setLoadingReferencias(false);
    }
  }, []);

  const fetchCategorias = useCallback(async () => {
    try {
      setLoading(true);
      const skip = page * rowsPerPage;
      const limit = rowsPerPage;
      
      const data = await CategoriasService.getCategorias(filter, skip, limit);
      setCategorias(data);
      
      // Obter contagem total para paginação
      const count = await CategoriasService.getCategoriasCount(filter);
      setTotalCount(count);
    } catch (error) {
      console.error('Erro ao buscar categorias:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao buscar categorias',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, filter]);

  useEffect(() => {
    // Buscar categorias ao iniciar a página ou mudar filtros/paginação
    fetchCategorias();
    // Carregar referências
    carregarReferencias();
  }, [fetchCategorias, carregarReferencias]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterChange = (field: keyof CategoriaFilter, value: string | number | undefined) => {
    // Limpar timeout anterior se existir
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Atualizar o filtro imediatamente na UI
    setFilter((prev) => ({
      ...prev,
      [field]: value || undefined, // Use undefined para remover o filtro vazio
    }));

    // Debounce: aguardar 500ms antes de aplicar a busca
    searchTimeoutRef.current = setTimeout(() => {
      setPage(0); // Resetar paginação ao mudar filtro
    }, 500);
  };

  // Função para limpar filtro de busca
  const clearSearchFilter = () => {
    setFilter((prev) => ({
      ...prev,
      nome: undefined
    }));
    setPage(0);
  };

  const handleCreateCategoria = async () => {
    try {
      // Verificar campos obrigatórios
      if (!newCategoria.nome) {
        setSnackbar({
          open: true,
          message: 'Nome da categoria é obrigatório',
          severity: 'error',
        });
        return;
      }

      const result = await CategoriasService.createCategoria(newCategoria);
      setOpenCreateDialog(false);
      setSnackbar({
        open: true,
        message: 'Categoria criada com sucesso',
        severity: 'success',
      });
      
      // Limpar formulário
      setNewCategoria({ nome: '' });
      
      // Recarregar dados
      fetchCategorias();
      
      // Atualizar categorias principais para seleção
      carregarReferencias();
    } catch (error) {
      console.error('Erro ao criar categoria:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao criar categoria',
        severity: 'error',
      });
    }
  };

  const handleOpenEditDialog = (categoria: Categoria) => {
    setCurrentCategoria(categoria);
    // Inicializar dados para edição
    setEditCategoria({
      nome: categoria.nome,
      setor_id: categoria.setor_id,
      principal_id: categoria.principal_id
    });
    
    setOpenEditDialog(true);
  };

  const handleEditCategoria = async () => {
    if (!currentCategoria) return;

    try {
      // Verificar campos obrigatórios
      if (!editCategoria.nome) {
        setSnackbar({
          open: true,
          message: 'Nome da categoria é obrigatório',
          severity: 'error',
        });
        return;
      }

      await CategoriasService.updateCategoria(currentCategoria.id, editCategoria);
      setOpenEditDialog(false);
      setSnackbar({
        open: true,
        message: 'Categoria atualizada com sucesso',
        severity: 'success',
      });
      
      // Limpar estados
      setCurrentCategoria(null);
      setEditCategoria({});
      
      // Recarregar dados
      fetchCategorias();
      
      // Atualizar categorias principais para seleção
      carregarReferencias();
    } catch (error) {
      console.error('Erro ao atualizar categoria:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao atualizar categoria',
        severity: 'error',
      });
    }
  };

  const handleOpenDeleteDialog = (categoria: Categoria) => {
    setCurrentCategoria(categoria);
    setOpenDeleteDialog(true);
  };

  const handleDeleteCategoria = async () => {
    if (!currentCategoria) return;

    try {
      await CategoriasService.deleteCategoria(currentCategoria.id);
      setOpenDeleteDialog(false);
      setSnackbar({
        open: true,
        message: 'Categoria excluída com sucesso',
        severity: 'success',
      });
      
      // Limpar estados
      setCurrentCategoria(null);
      
      // Recarregar dados
      fetchCategorias();
      
      // Atualizar categorias principais para seleção
      carregarReferencias();
    } catch (error) {
      console.error('Erro ao excluir categoria:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao excluir categoria',
        severity: 'error',
      });
    }
  };

  // Handler para alterações em selects
  const handleSelectChange = (event: SelectChangeEvent<number | string | undefined>, setter: Function) => {
    const value = event.target.value === '' ? undefined : Number(event.target.value);
    setter((prev: any) => ({
      ...prev,
      [event.target.name]: value
    }));
  };

  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({
      ...prev,
      open: false,
    }));
  };

  return (
    <SidebarLayout>
      <Box p={3}>
        <Typography variant="h4" gutterBottom display="flex" alignItems="center">
          <CategoryIcon sx={{ mr: 2 }} />
          Gerenciamento de Categorias
        </Typography>
        
        {/* Área de filtros */}
        <Paper 
          elevation={2} 
          sx={{ p: 2, mb: 3, backgroundColor: theme.palette.background.default }}
        >
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Buscar por nome"
                variant="outlined"
                size="small"
                value={filter.nome || ''}
                onChange={(e) => handleFilterChange('nome', e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  endAdornment: filter.nome ? (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={clearSearchFilter}
                        edge="end"
                      >
                        <ClearIcon />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel id="setor-filter-label">Setor</InputLabel>
                <Select
                  labelId="setor-filter-label"
                  label="Setor"
                  name="setor_id"
                  value={filter.setor_id || ''}
                  onChange={(e) => handleFilterChange('setor_id', e.target.value === '' ? undefined : Number(e.target.value))}
                >
                  <MenuItem value="">Todos</MenuItem>
                  {setores.map((setor) => (
                    <MenuItem key={setor.id} value={setor.id}>
                      {setor.nome}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setOpenCreateDialog(true)}
                sx={{ height: '100%' }}
              >
                Nova Categoria
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {/* Tabela de Categorias */}
        <Paper elevation={3} sx={{ width: '100%', overflow: 'hidden' }}>
          <TableContainer sx={{ maxHeight: 'calc(100vh - 280px)' }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Nome</TableCell>
                  <TableCell>Setor</TableCell>
                  <TableCell>Categoria Principal</TableCell>
                  <TableCell align="right">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                ) : categorias.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      Nenhuma categoria encontrada
                    </TableCell>
                  </TableRow>
                ) : (
                  categorias.map((categoria) => (
                    <TableRow key={categoria.id} hover>
                      <TableCell>{categoria.id}</TableCell>
                      <TableCell>{categoria.nome}</TableCell>
                      <TableCell>
                        {setores.find(s => s.id === categoria.setor_id)?.nome || '-'}
                      </TableCell>
                      <TableCell>
                        {categoriasPrincipais.find(c => c.id === categoria.principal_id)?.nome || '-'}
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleOpenEditDialog(categoria)}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleOpenDeleteDialog(categoria)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
      </Box>

      {/* Modal de Criação */}
      <Dialog
        open={openCreateDialog}
        onClose={() => setOpenCreateDialog(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Nova Categoria</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Nome"
                name="nome"
                required
                value={newCategoria.nome || ''}
                onChange={(e) => setNewCategoria({ ...newCategoria, nome: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel id="new-setor-label">Setor</InputLabel>
                <Select
                  labelId="new-setor-label"
                  label="Setor"
                  name="setor_id"
                  value={newCategoria.setor_id || ''}
                  onChange={(e) => handleSelectChange(e, setNewCategoria)}
                >
                  <MenuItem value="">Selecione...</MenuItem>
                  {setores.map((setor) => (
                    <MenuItem key={setor.id} value={setor.id}>
                      {setor.nome}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel id="new-categoria-principal-label">Categoria Principal</InputLabel>
                <Select
                  labelId="new-categoria-principal-label"
                  label="Categoria Principal"
                  name="principal_id"
                  value={newCategoria.principal_id || ''}
                  onChange={(e) => handleSelectChange(e, setNewCategoria)}
                >
                  <MenuItem value="">Selecione...</MenuItem>
                  {categoriasPrincipais.map((categoria) => (
                    <MenuItem key={categoria.id} value={categoria.id}>
                      {categoria.nome}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCreateDialog(false)}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleCreateCategoria}
            disabled={!newCategoria.nome}
          >
            Criar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Edição */}
      <Dialog
        open={openEditDialog}
        onClose={() => setOpenEditDialog(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Editar Categoria</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Nome"
                name="nome"
                required
                value={editCategoria.nome || ''}
                onChange={(e) => setEditCategoria({ ...editCategoria, nome: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel id="edit-setor-label">Setor</InputLabel>
                <Select
                  labelId="edit-setor-label"
                  label="Setor"
                  name="setor_id"
                  value={editCategoria.setor_id || ''}
                  onChange={(e) => handleSelectChange(e, setEditCategoria)}
                >
                  <MenuItem value="">Selecione...</MenuItem>
                  {setores.map((setor) => (
                    <MenuItem key={setor.id} value={setor.id}>
                      {setor.nome}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel id="edit-categoria-principal-label">Categoria Principal</InputLabel>
                <Select
                  labelId="edit-categoria-principal-label"
                  label="Categoria Principal"
                  name="principal_id"
                  value={editCategoria.principal_id || ''}
                  onChange={(e) => handleSelectChange(e, setEditCategoria)}
                >
                  <MenuItem value="">Selecione...</MenuItem>
                  {categoriasPrincipais
                    .filter(categoria => currentCategoria ? categoria.id !== currentCategoria.id : true)
                    .map((categoria) => (
                      <MenuItem key={categoria.id} value={categoria.id}>
                        {categoria.nome}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEditDialog(false)}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleEditCategoria}
            disabled={!editCategoria.nome}
          >
            Salvar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Confirmação de Exclusão */}
      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Confirmar Exclusão</DialogTitle>
        <DialogContent>
          <Typography>
            Tem certeza que deseja excluir a categoria "{currentCategoria?.nome}"?
            Esta ação não pode ser desfeita.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>Cancelar</Button>
          <Button variant="contained" color="error" onClick={handleDeleteCategoria}>
            Excluir
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar para mensagens */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </SidebarLayout>
  );
};

export default CategoriasPage;
